import { useSelectedLadderOrdersStore } from '../../Store/selectedLadderOrdersStore';
import { fetchBondLadderOrders } from '../../http/api';

/**
 * Fetch latest ladder orders and reconcile against local selected/original orders.
 * - Remove rungs absent in backend (treat as inactive).
 * - For backend-locked rungs (status DONE or protocol RFQ): update original, lock editing, and copy to selected.
 * - Return rung numbers (1-based) that transitioned to locked (for info modal).
 */
export const checkBondLadderRaceConditions = async (token, bondLadderId) => {
  let backendOrders;  
  try {  
    backendOrders = await fetchBondLadderOrders(token, bondLadderId);  
  } catch (error) {  
    console.error('Failed to fetch bond ladder orders:', error);  
    return { warnRungs: [], addedRungs: [], backendOrders: [] }; // Return a safe response  
  }  
  const orders = Array.isArray(backendOrders) ? backendOrders : [];

  const store = useSelectedLadderOrdersStore.getState();
  const newOriginal = { ...store.originalSelectedOrders };
  const warnRungs = [];
  const addedRungs = []; // NEW

  // Build set of backend rung indices (0-based) to infer inactive removals
  const backendRungSet = new Set(
    orders
      .filter(o => o && o.bondLadderRungId != null)
      .map(o => Number(o.bondLadderRungId) - 1)
  );

  // Remove any rung present locally but missing in backend (inactive)
  Object.keys(newOriginal).forEach(key => {
    const idx = Number(key);
    if (!backendRungSet.has(idx)) {
      delete newOriginal[idx];
    }
  });

  // Process backend-locked (DONE or RFQ) status; lock and copy to selected
  orders.forEach(o => {
    if (!o || o.bondLadderRungId == null) return;
    const rungIndex = Number(o.bondLadderRungId) - 1;

    const backendStatus = (o.status || '').toString().toUpperCase();
    const backendProtocol = (o.protocol || '').toString().toUpperCase();
    const backendLocked = backendStatus === 'DONE' || backendProtocol === 'RFQ';

    const localStatus = (newOriginal?.[rungIndex]?.status || '').toString().toUpperCase();
    const localProtocol = (newOriginal?.[rungIndex]?.protocol || '').toString().toUpperCase();
    const localLocked = newOriginal?.[rungIndex]?.isEditable === false || localStatus === 'DONE' || localProtocol === 'RFQ';

    if (backendLocked) {
      const updated = {
        ...newOriginal[rungIndex],
        ...o,
        qty: o.quantity,
        quantity: o.quantity,
        isEditable: false,
        action: o.side || o.action,
        status: o.status,
        protocol: o.protocol
      };
      newOriginal[rungIndex] = updated;

      if (typeof store.selectOrder === 'function') {
        store.selectOrder(rungIndex, updated);
      }

      if (!localLocked) {
        warnRungs.push(rungIndex + 1); // 1-based for UI
      }
    }
  });

  // NEW: Add or update any backend order (including UNLOCKED) that is not yet in originalSelectedOrders
  orders.forEach(o => {
    if (!o || o.bondLadderRungId == null) return;
    const rungIndex = Number(o.bondLadderRungId) - 1;

    // Skip if already present (either previously local or just processed as locked)
    if (newOriginal[rungIndex]) return;

    const backendStatus = (o.status || '').toString().toUpperCase();
    const backendProtocol = (o.protocol || '').toString().toUpperCase();
    const backendLocked = backendStatus === 'DONE' || backendProtocol === 'RFQ';

    const updated = {
      ...o,
      qty: o.quantity,
      quantity: o.quantity,
      isEditable: !backendLocked,
      action: o.side || o.action,
      status: o.status,
      protocol: o.protocol,
      side: o.side || o.action
    };
    newOriginal[rungIndex] = updated;
    addedRungs.push(rungIndex + 1); // store 1-based index for UI

    // NEW: also add to selectedOrders so UI reflects freshly added backend order
    if (typeof store.selectOrder === 'function') {
      store.selectOrder(rungIndex, updated);
    }
  });

  // Persist reconciled originalSelectedOrders
  useSelectedLadderOrdersStore.setState({ originalSelectedOrders: newOriginal });

  return { warnRungs, addedRungs, backendOrders: orders }; // UPDATED
};
